import { CityData, CityAnalysisData, Insight } from './CityAnalysisChart.types'

export const formatPricePerSqm = (price: number): string => {
  return `CHF ${Math.round(price).toLocaleString()}/m²`
}

export const formatShortPricePerSqm = (price: number): string => {
  if (price >= 10000) {
    return `${(price / 1000).toFixed(0)}k`
  }
  return Math.round(price).toLocaleString()
}

export const processCityData = (aggregations: any): CityAnalysisData => {
  if (!aggregations?.cities?.buckets) {
    return {
      cities: [],
      dealType: 'purchase',
      timestamp: new Date().toISOString(),
    }
  }

  const MIN_LISTINGS_PER_CITY = 30

  const cities = aggregations.cities.buckets
    .filter((bucket: any) => bucket.doc_count >= MIN_LISTINGS_PER_CITY)
    .map((bucket: any): CityData => {
      const percentiles = bucket.price_percentiles?.values || {}
      const stats = bucket.price_stats || {}

      // Process property types if available
      const propertyTypes =
        bucket.property_types?.buckets?.map((typeBucket: any) => ({
          type: typeBucket.key,
          median: typeBucket.median_price?.values?.['50.0'] || 0,
        })) || []

      return {
        city: bucket.key,
        count: bucket.doc_count,
        stats: {
          min: stats.min || 0,
          max: stats.max || 0,
          avg: stats.avg || 0,
          std_deviation: stats.std_deviation || 0,
        },
        percentiles: {
          p5: percentiles['5.0'] || 0,
          p10: percentiles['10.0'] || 0,
          p25: percentiles['25.0'] || 0,
          p50: percentiles['50.0'] || 0,
          p75: percentiles['75.0'] || 0,
          p90: percentiles['90.0'] || 0,
          p95: percentiles['95.0'] || 0,
        },
        propertyTypes,
      }
    })
    .slice(0, 30) // Limit to top 30 cities

  return {
    cities,
    dealType: 'purchase',
    timestamp: new Date().toISOString(),
  }
}

export const calculateKPIs = (data: CityAnalysisData) => {
  if (data.cities.length === 0) {
    return {
      totalCities: 0,
      avgPricePerSqm: 0,
      highestCity: null,
      lowestCity: null,
      totalListings: 0,
    }
  }

  const totalListings = data.cities.reduce((sum, city) => sum + city.count, 0)
  const avgPricePerSqm =
    data.cities.reduce(
      (sum, city) => sum + city.percentiles.p50 * city.count,
      0
    ) / totalListings

  const sortedByPrice = [...data.cities].sort(
    (a, b) => b.percentiles.p50 - a.percentiles.p50
  )
  const highestCity = sortedByPrice[0]
  const lowestCity = sortedByPrice[sortedByPrice.length - 1]

  return {
    totalCities: data.cities.length,
    avgPricePerSqm,
    highestCity,
    lowestCity,
    totalListings,
  }
}

export const getChartColors = () => ({
  primary: '#00ade6',
  primaryLight: '#66d3f3',
  primaryDark: '#008bb8',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
})

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

export const generateInsights = (
  data: CityAnalysisData,
  t: TranslationFn = defaultT
): Insight[] => {
  const insights: Insight[] = []

  if (data.cities.length === 0) return insights

  const sortedByMedian = [...data.cities].sort(
    (a, b) => b.percentiles.p50 - a.percentiles.p50
  )

  // 1. Price range analysis
  const highest = sortedByMedian[0]
  const lowest = sortedByMedian[sortedByMedian.length - 1]
  const priceRange = highest.percentiles.p50 - lowest.percentiles.p50
  const rangePercent = (priceRange / lowest.percentiles.p50) * 100

  insights.push({
    title: t('charts.insights.city.cityPriceRange'),
    description: t('charts.insights.descriptions.city.cityPriceRange', {
      variation: rangePercent.toFixed(0),
      cityHigh: highest.city,
      priceHigh: formatPricePerSqm(highest.percentiles.p50),
      cityLow: lowest.city,
      priceLow: formatPricePerSqm(lowest.percentiles.p50),
    }),
  })

  // 2. Market distribution pattern
  const medianPrices = data.cities.map((c) => c.percentiles.p50)
  const avgMedian =
    medianPrices.reduce((a, b) => a + b, 0) / medianPrices.length
  const aboveAvg = data.cities.filter((c) => c.percentiles.p50 > avgMedian)

  insights.push({
    title: t('charts.insights.city.marketDistribution'),
    description: t('charts.insights.descriptions.city.marketDistribution', {
      count: aboveAvg.length,
      average: formatPricePerSqm(avgMedian),
      percentage: ((aboveAvg.length / data.cities.length) * 100).toFixed(0),
    }),
  })

  // 3. Price variability within cities
  const cityVariabilities = data.cities
    .map((city) => {
      const iqr = city.percentiles.p75 - city.percentiles.p25
      const median = city.percentiles.p50
      const variabilityRatio = (iqr / median) * 100
      return { city: city.city, variability: variabilityRatio }
    })
    .sort((a, b) => a.variability - b.variability)

  const mostStable = cityVariabilities[0]
  const mostVariable = cityVariabilities[cityVariabilities.length - 1]

  insights.push({
    title: t('charts.insights.city.priceConsistency'),
    description: t('charts.insights.descriptions.city.priceConsistency', {
      city: mostStable.city,
      stdDev: mostStable.variability.toFixed(0) + '%',
    }),
  })

  // 4. Volume concentration
  const totalListings = data.cities.reduce((sum, city) => sum + city.count, 0)
  const top5Cities = [...data.cities]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
  const top5Volume = top5Cities.reduce((sum, city) => sum + city.count, 0)
  const volumeConcentration = (top5Volume / totalListings) * 100

  const topCity = top5Cities[0]
  insights.push({
    title: t('charts.insights.city.marketActivity'),
    description: t('charts.insights.descriptions.city.marketActivity', {
      percentage: volumeConcentration.toFixed(0),
      topCity: topCity.city,
      topCount: topCity.count.toLocaleString(),
    }),
  })

  // 5. Premium segment analysis
  const citiesWithP90 = data.cities.filter((c) => c.percentiles.p90 > 0)
  if (citiesWithP90.length > 0) {
    const highestP90 = citiesWithP90.reduce((max, city) =>
      city.percentiles.p90 > max.percentiles.p90 ? city : max
    )
    const premiumGap =
      ((highestP90.percentiles.p90 - highestP90.percentiles.p50) /
        highestP90.percentiles.p50) *
      100

    insights.push({
      title: t('charts.insights.city.premiumSegment'),
      description: t('charts.insights.descriptions.city.premiumSegment', {
        city: highestP90.city,
        percentage: premiumGap.toFixed(0),
        threshold: formatPricePerSqm(highestP90.percentiles.p90),
      }),
    })
  }

  return insights
}
